/**
 * Minimum order quantity (MOQ) enforcement.
 *
 * Enforcement happens at the CART API BOUNDARY, not in the DOM. Every cart
 * mutation on this theme goes through one of a handful of endpoints — the cart
 * page's stepper, for instance, calls:
 *
 *   fetch(`${window.Theme.routes.cart_change_url}.js`, {
 *     method: 'POST', body: JSON.stringify({ id: key, quantity })
 *   })
 *
 * Patching `fetch` and `XMLHttpRequest` means a sub-minimum quantity is raised
 * no matter which button, handler, or app triggered it. An earlier version of
 * this file tried to pre-empt the theme's own click handlers in the capture
 * phase; that depended on which component rendered the stepper and on the
 * attributes landing on the input, and it did not hold up on the cart page.
 *
 * Floors come from `snippets/moq-data.liquid`, which prints them as a JSON
 * blob — inspectable in view-source, and independent of the markup around any
 * particular quantity input.
 *
 * Wrapped in an IIFE: this loads as a classic script alongside third-party app
 * scripts, and a top-level `const` colliding with one of theirs would throw.
 */

;(function () {
  'use strict'

  const DATA_ID = 'moq-data'
  const MESSAGE_CLASS = 'quantity-selector__moq-message'
  const MESSAGE_TIMEOUT = 6000

  const selectors = {
    selector: '[data-quantity-selector]',
    input: '[data-quantity-input]',
    minusWrapper: '[data-button-wrapper-minus]',
    cartItem: '[data-cartitem-key]',
    productForm: '[data-product-form]',
    notice: '.moq-notice'
  }

  const data = readData()

  function readData() {
    const empty = { product: { min: 1, uom: '' }, order: [], lines: {}, variants: {} }
    const el = document.getElementById(DATA_ID)

    if (!el) return empty

    let parsed

    try {
      parsed = JSON.parse(el.textContent)
    } catch (error) {
      console.error('[MOQ] Could not parse moq-data — no floors will be enforced:', error)
      return empty
    }

    // The blob is arrays; the lookups below want maps.
    const lines = {}
    const order = []

    ;(parsed.lines || []).forEach(line => {
      lines[line.key] = { min: line.min, uom: line.uom }
      order.push(line.key)
    })

    const variants = {}

    ;(parsed.variants || []).concat(parsed.productVariants || []).forEach(variant => {
      variants[String(variant.id)] = { min: variant.min, uom: variant.uom }
    })

    return { product: parsed.product || { min: 1, uom: '' }, order, lines, variants }
  }

  /**
   * The floor that applies to a given quantity input: its cart line's, or the
   * current product's when the input is on a product page.
   */
  function entryForInput(input) {
    const line = input.closest(selectors.cartItem)

    if (line) {
      return data.lines[line.dataset.cartitemKey] || { min: 1, uom: '' }
    }

    return data.product || { min: 1, uom: '' }
  }

  function floorForLine(key) {
    const entry = data.lines[key]
    return entry ? entry.min : 1
  }

  function floorForVariant(id) {
    const entry = data.variants[String(id)]
    return entry ? entry.min : 1
  }

  /**
   * A cart identifier may be a line key ("12345:abc"), a variant id, or a
   * 1-based line index, depending on which endpoint and which caller.
   */
  function floorForId(id) {
    if (id === undefined || id === null) return 1

    const key = String(id)

    if (data.lines[key]) return data.lines[key].min
    if (data.variants[key]) return data.variants[key].min

    return 1
  }

  function floorForLineNumber(line) {
    const key = data.order[parseInt(line, 10) - 1]
    return key ? floorForLine(key) : 1
  }

  function describe(min, uom) {
    return `Minimum order quantity is ${min}${uom ? ` ${uom}` : ''}.`
  }

  function messageForId(id) {
    const key = String(id)
    const entry = data.lines[key] || data.variants[key]
    return entry ? describe(entry.min, entry.uom) : ''
  }

  // ── Clamping the request payloads ──────────────────────────────────────

  let lastClamp = null

  function note(id, requested, min) {
    lastClamp = { id, requested, min, message: messageForId(id) }
    console.warn(`[MOQ] Raised quantity ${requested} to the minimum of ${min}.`)
    showMessage(id)
  }

  /**
   * Quantity 0 means "remove this line" and must always pass through.
   */
  function clampQuantity(quantity, min, id) {
    const value = parseInt(quantity, 10)

    if (isNaN(value) || value === 0 || value >= min) return null

    note(id, value, min)
    return min
  }

  /** `{ id | line, quantity }` — /cart/change */
  function clampChange(payload) {
    if (!payload || payload.quantity === undefined) return false

    const min =
      payload.id !== undefined ? floorForId(payload.id) : floorForLineNumber(payload.line)

    const clamped = clampQuantity(payload.quantity, min, payload.id)
    if (clamped === null) return false

    payload.quantity = clamped
    return true
  }

  /** `{ id, quantity }` — /cart/add */
  function clampAdd(payload) {
    if (!payload || payload.quantity === undefined) return false

    const clamped = clampQuantity(payload.quantity, floorForVariant(payload.id), payload.id)
    if (clamped === null) return false

    payload.quantity = clamped
    return true
  }

  /** `{ updates: { key: qty } }` or `{ updates: [qty, qty] }` — /cart/update */
  function clampUpdate(payload) {
    if (!payload || !payload.updates) return false

    let changed = false
    const updates = payload.updates

    if (Array.isArray(updates)) {
      updates.forEach((quantity, index) => {
        const key = data.order[index]
        if (!key) return

        const clamped = clampQuantity(quantity, floorForLine(key), key)
        if (clamped !== null) {
          updates[index] = clamped
          changed = true
        }
      })
    } else {
      Object.keys(updates).forEach(key => {
        const clamped = clampQuantity(updates[key], floorForId(key), key)
        if (clamped !== null) {
          updates[key] = clamped
          changed = true
        }
      })
    }

    return changed
  }

  function clampPayload(path, payload) {
    if (path.indexOf('/cart/change') !== -1) return clampChange(payload)
    if (path.indexOf('/cart/add') !== -1) return clampAdd(payload)
    if (path.indexOf('/cart/update') !== -1) return clampUpdate(payload)
    return false
  }

  function isCartWrite(url) {
    const path = String(url || '')
    return (
      path.indexOf('/cart/change') !== -1 ||
      path.indexOf('/cart/add') !== -1 ||
      path.indexOf('/cart/update') !== -1
    )
  }

  // ── Body encodings ─────────────────────────────────────────────────────
  // Callers use JSON, form-encoded strings, URLSearchParams, or FormData.

  function clampJson(path, body) {
    let payload

    try {
      payload = JSON.parse(body)
    } catch (error) {
      return null
    }

    return clampPayload(path, payload) ? JSON.stringify(payload) : null
  }

  /** `updates[key]=n` / `id=..&quantity=n` */
  function clampParams(path, params) {
    const payload = {}
    const updates = {}
    let hasUpdates = false

    params.forEach((value, key) => {
      const match = key.match(/^updates\[(.+)\]$/)
      if (match) {
        updates[match[1]] = value
        hasUpdates = true
      } else {
        payload[key] = value
      }
    })

    if (hasUpdates) payload.updates = updates
    if (!clampPayload(path, payload)) return false

    if (hasUpdates) {
      Object.keys(payload.updates).forEach(key => params.set(`updates[${key}]`, payload.updates[key]))
    }
    if (payload.quantity !== undefined) params.set('quantity', payload.quantity)

    return true
  }

  function clampFormData(path, form) {
    const params = new URLSearchParams()
    form.forEach((value, key) => {
      if (typeof value === 'string') params.append(key, value)
    })

    if (!clampParams(path, params)) return false

    params.forEach((value, key) => form.set(key, value))
    return true
  }

  function clampBody(path, body) {
    if (typeof body === 'string') {
      const asJson = clampJson(path, body)
      if (asJson !== null) return asJson

      const params = new URLSearchParams(body)
      return clampParams(path, params) ? params.toString() : body
    }

    if (typeof URLSearchParams !== 'undefined' && body instanceof URLSearchParams) {
      clampParams(path, body)
      return body
    }

    if (typeof FormData !== 'undefined' && body instanceof FormData) {
      clampFormData(path, body)
      return body
    }

    return body
  }

  // ── fetch ──────────────────────────────────────────────────────────────

  const nativeFetch = window.fetch

  if (typeof nativeFetch === 'function') {
    window.fetch = function (resource, init) {
      try {
        const url = typeof resource === 'string' ? resource : resource && resource.url

        if (isCartWrite(url) && init && init.body) {
          init = Object.assign({}, init, { body: clampBody(url, init.body) })
        }
      } catch (error) {
        console.error('[MOQ] fetch guard failed, letting the request through:', error)
      }

      return nativeFetch.apply(this, [resource, init])
    }
  }

  // ── XMLHttpRequest (jQuery-based callers) ──────────────────────────────

  const nativeOpen = XMLHttpRequest.prototype.open
  const nativeSend = XMLHttpRequest.prototype.send

  XMLHttpRequest.prototype.open = function (method, url) {
    this.__moqUrl = url
    return nativeOpen.apply(this, arguments)
  }

  XMLHttpRequest.prototype.send = function (body) {
    try {
      if (body && isCartWrite(this.__moqUrl)) {
        arguments[0] = clampBody(this.__moqUrl, body)
      }
    } catch (error) {
      console.error('[MOQ] XHR guard failed, letting the request through:', error)
    }

    return nativeSend.apply(this, arguments)
  }

  // ── Presentation ───────────────────────────────────────────────────────
  // Cosmetic only. Enforcement above does not depend on any of this.

  function inputForId(id) {
    const line = document.querySelector(`[data-cartitem-key="${id}"]`)
    return line ? line.querySelector(selectors.input) : document.querySelector(selectors.input)
  }

  /**
   * `snippets/moq-notice.liquid` already states the minimum next to most
   * quantity inputs. Drawing attention to that beats injecting a second line
   * saying the same thing — which is what this used to do, producing a stacked
   * duplicate in the cart.
   *
   * Returns false when there is no notice to flash, so the caller can fall back
   * to injecting one.
   */
  function flashNotice(input) {
    const scope = input.closest(selectors.cartItem) || input.closest(selectors.productForm)
    const notice = scope && scope.querySelector(selectors.notice)

    if (!notice) return false

    // Restart the animation even if it is already running.
    notice.classList.remove('moq-notice--alert')
    void notice.offsetWidth
    notice.classList.add('moq-notice--alert')

    clearTimeout(notice.moqTimeoutId)
    notice.moqTimeoutId = setTimeout(() => notice.classList.remove('moq-notice--alert'), MESSAGE_TIMEOUT)

    return true
  }

  /** Fallback for quantity inputs with no notice rendered beside them. */
  function injectMessage(input, text) {
    const wrapper = input.closest(selectors.selector) || input.parentNode
    if (!wrapper) return

    let message = wrapper.querySelector(`.${MESSAGE_CLASS}`)

    if (!message) {
      message = document.createElement('p')
      message.className = MESSAGE_CLASS
      message.setAttribute('role', 'status')
      wrapper.appendChild(message)
    }

    message.textContent = text

    clearTimeout(message.moqTimeoutId)
    message.moqTimeoutId = setTimeout(() => message.remove(), MESSAGE_TIMEOUT)
  }

  function announce(input, text) {
    if (!input) return
    if (flashNotice(input)) return

    injectMessage(input, text)
  }

  function showMessage(id) {
    announce(inputForId(id), messageForId(id))
  }

  /** Stamp the floor onto each cart input so the browser and the theme agree. */
  function applyFloors() {
    document.querySelectorAll(selectors.cartItem).forEach(line => {
      const input = line.querySelector(selectors.input)
      if (!input) return

      const min = floorForLine(line.dataset.cartitemKey)
      input.setAttribute('min', min)
      input.dataset.moq = min

      const wrapper = input.closest(selectors.selector)
      const minusWrapper = wrapper && wrapper.querySelector(selectors.minusWrapper)
      if (!minusWrapper) return

      const value = parseInt(input.value, 10)
      minusWrapper.classList.toggle(
        'quantity-selector__button-wrapper--disabled',
        isNaN(value) || value <= min
      )
    })
  }

  // ── Instant feedback ───────────────────────────────────────────────────
  // Without this the network clamp still holds, but the stepper visibly dips
  // to 264 before the server response snaps it back. These capture-phase
  // listeners run ahead of the theme's own handlers, which are bound directly
  // to the input and buttons.

  function showMessageFor(input) {
    const entry = entryForInput(input)
    announce(input, describe(entry.min, entry.uom))
  }

  function inputNear(target) {
    if (!target || typeof target.closest !== 'function') return null

    const wrapper = target.closest(selectors.selector)
    return wrapper ? wrapper.querySelector(selectors.input) : null
  }

  document.addEventListener(
    'click',
    event => {
      const minus = event.target.closest && event.target.closest('[data-quantity-minus]')
      if (!minus) return

      const input = inputNear(minus)
      if (!input) return

      const value = parseInt(input.value, 10)
      if (isNaN(value) || value > entryForInput(input).min) return

      event.preventDefault()
      event.stopPropagation()
      event.stopImmediatePropagation()

      showMessageFor(input)
      applyFloors()
    },
    true
  )

  // Mid-typing, a partial entry reads below the floor ("300" passes through
  // "3"). Swallow those so no sub-minimum update is queued; the value itself is
  // left alone, since rewriting it mid-keystroke fights the customer.
  document.addEventListener(
    'keyup',
    event => {
      const input = event.target
      if (!input.matches || !input.matches(selectors.input)) return

      const value = parseInt(input.value, 10)
      if (!isNaN(value) && value >= entryForInput(input).min) return

      event.stopPropagation()
      event.stopImmediatePropagation()
    },
    true
  )

  document.addEventListener(
    'change',
    event => {
      const input = event.target
      if (!input.matches || !input.matches(selectors.input)) return

      const min = entryForInput(input).min
      const value = parseInt(input.value, 10)

      if (!isNaN(value) && value >= min) return

      input.value = min
      showMessageFor(input)
      applyFloors()
    },
    true
  )

  function onReady() {
    applyFloors()

    const observer = new MutationObserver(() => {
      clearTimeout(observer.moqTimeoutId)
      observer.moqTimeoutId = setTimeout(applyFloors, 50)
    })

    observer.observe(document.documentElement, { childList: true, subtree: true })
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', onReady)
  } else {
    onReady()
  }

  // Debug handle — the IIFE hides everything else from the console.
  window.__moq = {
    loaded: true,
    data,
    floorForId,
    lastClamp: () => lastClamp
  }
})()
